/**
 * Ручной набор: разбор введённого номера и признак «звонок не по карточке».
 *
 * Вынесено из компонента отдельным модулем ради теста. Ошибка здесь не падает и
 * не видна на экране: «8 999 …» без приведения к +7 уедет в диалплан как есть,
 * и звонок либо не пройдёт, либо уйдёт не туда — за счёт компании.
 *
 * Правило нормализации не своё, а общее с сервером (normalizeRuPhone из @cc/shared):
 * вторая реализация в браузере разошлась бы с тем, что лежит в базе (§6.8), и один
 * номер выглядел бы в наборе и в карточке по-разному.
 */

import { MANUAL_CALL_MARKER, normalizeRuPhone } from '@cc/shared';
import type { SoftphoneCallSnapshot } from '@cc/softphone-core';

import { errorText } from '../../shared/lib/error-text.js';

/** Почему номер не принят. Текст для человека даёт manualDialErrorText */
export type ManualDialError = 'empty' | 'unexpected_chars' | 'not_ru_phone';

export type ManualDialParse =
  | { ok: true; phone: string }
  | { ok: false; error: ManualDialError };

/** Что человек имеет право набрать: цифры и разделители из любого способа записи */
const ALLOWED_CHARS_RE = /^[0-9+()\-.\s]+$/;

/**
 * Введённое → E.164. Принимает 8…, +7…, со скобками и пробелами — номер диктуют
 * по телефону и вставляют из письма, требовать один формат значит терять звонки.
 *
 * Это только предпросмотр и защита от заведомо пустого запроса: набирается тот
 * номер, который вернул сервер (deskManualCallResponseSchema.phone). Правило одно
 * и то же, но авторитет один — серверный, иначе появится второй ответ на вопрос
 * «какой номер уехал в диалплан».
 */
export function parseManualPhone(input: string): ManualDialParse {
  const trimmed = input.trim();
  if (trimmed === '') return { ok: false, error: 'empty' };

  // Буквы отсекаются до нормализации намеренно: normalizeRuPhone выкидывает
  // всё нечисловое молча, и «89991234567 доб.5» превратился бы в чужой номер
  if (!ALLOWED_CHARS_RE.test(trimmed)) return { ok: false, error: 'unexpected_chars' };

  const phone = normalizeRuPhone(trimmed);
  if (phone === null) return { ok: false, error: 'not_ru_phone' };

  return { ok: true, phone };
}

const ERROR_TEXTS: Record<ManualDialError, string> = {
  empty: 'Введите номер: 11 цифр, например 8 999 123-45-67',
  unexpected_chars: 'В номере есть лишние символы. Оставьте цифры, плюс, скобки и дефис',
  not_ru_phone: 'Не похоже на номер: нужно 11 цифр, начиная с +7 или 8',
};

export function manualDialErrorText(error: ManualDialError): string {
  return ERROR_TEXTS[error];
}

/**
 * Идёт ли сейчас (или только что закончился) звонок, набранный руками.
 *
 * Признак — значение X-Assignment-Id, выданное сервером: MANUAL_CALL_MARKER
 * ('manual'). Спутать его с настоящей карточкой нечем — идентификаторы назначений
 * это bigint, словом они не бывают (§7.3).
 */
export function isManualCall(call: SoftphoneCallSnapshot | null): boolean {
  return call !== null && call.assignmentId === MANUAL_CALL_MARKER;
}

/**
 * Подсказка про «свои номера» (24.09.2026). До первого набора карточкой лимит
 * пульту неизвестен — число не выдумываем, говорим, что он есть. После — точный
 * остаток из ответа сервера.
 */
export function ownNumbersHint(own: { left: number; perDay: number } | null): string {
  if (own === null) return 'Таких номеров в день — ограниченное число.';
  return `Свой номер: осталось ${String(own.left)} из ${String(own.perDay)} на сегодня.`;
}

/**
 * Отказ сервера на ручной звонок, переведённый в текст.
 *
 * Показывается текст сервера: у всех отказов он уже написан для человека — и про
 * часовой лимит («ручных звонков в час: 20, уже сделано 20»), и про смену, и про
 * софтфон. Свой пересказ причины разошёлся бы с той, по которой реально отказали.
 * Так же и отказы «своего номера» (24.09.2026): own_number_dnc,
 * own_number_recent_lead, own_number_other_dispatcher, own_number_limit —
 * сервер уже пишет их для человека, с датой заявки и остатком лимита.
 */
export function manualCallErrorText(error: unknown): string | undefined {
  return errorText(error);
}
